import { yupResolver } from '@hookform/resolvers/yup';
import { isAfter } from 'date-fns';
import jsCookie from 'js-cookie';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';

import {
  useLoginAuthntication,
  useResetPassword,
} from '@/modules/auth/apiHooks';
import { useLoginFunctions } from '@/modules/auth/components/FormsContainer/Login/logic';
import { IAuth } from '@/modules/auth/types';
import {
  Button,
  ErrorMessage,
  InputField,
  Typography,
} from '@/shared/components/atoms';
import { cookieExpires } from '@/shared/constant/general';
import routes from '@/shared/constant/routes';
import { useAuth } from '@/shared/hooks/context/auth';

import Eye from '~/images/login/Eye.svg';
import EyeSlash from '~/images/login/EyeSlash.svg';
import FilledBuildings from '~/images/shared/FilledBuildings.svg';
import Phone from '~/images/shared/Phone.svg';

export const TAB_QUERY_INDICATOR = 'loginType';

const LoginInvitation = () => {
  const { query, push } = useRouter();
  const { key } = query;
  const { t } = useTranslation('login');
  const { login } = useAuth();

  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);

  const { mutate: resetPassword, isLoading: isResetPasswordLoading } =
    useResetPassword();
  const { mutate: loginAuthntication, data: loginAuthnticationData } =
    useLoginAuthntication();

  const { LoginInvitationValidationSchema, convertJWT } =
    useLoginFunctions('phoneNumber');
  const userData: IAuth.ILoginInvitationUserData | undefined = key
    ? convertJWT(String(key))
    : undefined;

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      identifier: userData?.phoneNumber,
      password: '',
      country: userData?.countryCode ?? 'EG',
    } as IAuth.ILoginSchema,
    resolver: yupResolver(LoginInvitationValidationSchema),
  });

  const {
    register,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = methods;

  const handleLogin = (password: string) => {
    {
      if (userData?.phoneNumber) {
        toast.success(t('password-setted-successfully'));
        loginAuthntication(
          {
            identifier: userData?.phoneNumber,
            password,
          },
          {
            onSuccess: (loginData: IAuth.ILoginRequestResponse) => {
              login(
                loginData.token,
                loginData.user,
                loginData.expiry ?? cookieExpires
              );
            },
          }
        );
      }
    }
  };

  const onSubmit = () => {
    const password = getValues('password');
    if (password) {
      resetPassword(password, {
        onSuccess: () => handleLogin(password),
      });
    }
  };

  useEffect(() => {
    if (userData) {
      setValue('identifier', userData.phoneNumber);
      const expiryDate = new Date(`${userData.exp}000`);
      if (isAfter(new Date(), expiryDate)) {
        toast.error(t('invitation-expired'));
        setTimeout(() => {
          push(routes.login);
        }, 3000);
      } else if (!loginAuthnticationData) {
        jsCookie.set('token', String(key), {
          path: '/',
          sameSite: 'Strict',
          expires: cookieExpires,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  return (
    <div className='flex h-full flex-col'>
      {userData && (
        <div className='ga-2 flex flex-col'>
          <div className='flex w-fit items-center gap-2 rounded-xl bg-primary-50 py-2 px-12'>
            <span>
              <FilledBuildings />
            </span>
            <span className='font-medium'>{userData.companyName}</span>
          </div>
          <div className='flex flex-col'>
            <Typography weight='semiBold' variant='h4'>
              {t('welcome', {
                name: userData.userName,
              })}
            </Typography>
            <Typography variant='caption' className='text-surface-10'>
              {t('company-invitation-description', {
                company: userData.companyName,
                role: userData.roleName,
              })}
            </Typography>
            <Typography variant='caption' className='text-surface-10'>
              {t('start-create-password')}
            </Typography>
          </div>
        </div>
      )}
      <FormProvider {...methods}>
        <form className='flex flex-col gap-16'>
          <div className='mt-16 flex flex-col gap-0'>
            <InputField
              label={t('mobile-number')}
              width='w-full'
              disabled
              id='identifier'
              name='identifier'
              required
              errorMessage={
                getValues('identifier') === null
                  ? t('this-field-is-required')
                  : errors?.identifier?.message
              }
              className='rounded-xl bg-background-transparent py-2'
              borderColor='border-outline-variant border-xs'
              placeholder={t('enter-mobile-number')}
              prefix={
                <span className='px-2 text-20 text-primary-default'>
                  <Phone />
                </span>
              }
              value={userData?.phoneNumber}
            />
          </div>
          <div className='flex flex-col gap-4'>
            <InputField
              borderColor='border-outline-variant'
              width='w-full'
              py='!py-14'
              label={t('password')}
              placeholder={t('password')}
              error={!!errors.password}
              className='!rounded-xl bg-background-transparent'
              type={passwordVisible ? 'text' : 'password'}
              suffix={
                <div
                  className='cursor-pointer'
                  onClick={() => setPasswordVisible((prev) => !prev)}
                >
                  {passwordVisible ? (
                    <Eye width='24' height='24' />
                  ) : (
                    <EyeSlash width='24' height='24' />
                  )}
                </div>
              }
              {...register('password')}
            />
            {errors?.password ? (
              <ErrorMessage
                errorMessage={errors.password.message ?? ''}
                className='mt-0'
              />
            ) : (
              <Typography className='text-surface-10' variant='caption'>
                {t('password-requirements')}
              </Typography>
            )}
          </div>

          <div className='flex flex-col gap-2'>
            <Button
              size='full'
              type='submit'
              onClick={handleSubmit(onSubmit)}
              className='mt-32 w-full !rounded-xl py-14 text-base font-semi-bold'
              disabled={isResetPasswordLoading}
            >
              {t('create-your-account')}
            </Button>
            <Typography
              className='mt-16 text-center text-surface-10'
              variant='caption'
            >
              <Trans
                t={t}
                i18nKey='termsAndConditionsAgreement'
                components={{
                  terms: (
                    <a href={routes.terms} className='font-medium underline' />
                  ),
                  policy: (
                    <a href={routes.policy} className='font-medium underline' />
                  ),
                }}
              />
            </Typography>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default LoginInvitation;
